import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import AppLayoutDoctor from '../../../navigation/Layout/LayoutDoctor';
import CalendarView from '../../components/calendarView/calendarView';
import WeekView from '../../components/weekView/weekView';
import EventsList from '../../components/eventsList/eventsList';
import AvailabilityPeriodsList from '../../components/availabilityPeriodsList/availabilityPeriodsList';
import useDisponibility from '../../hooks/useDisponibility';
import { useEvents } from '../../hooks/useEvents';
import './availabilityPage.css';

dayjs.extend(isBetween);

const SCROLL_LOCK_DURATION = 1200;

function normalizeEvent(e) {
  const from = dayjs(e.startTime);
  const to = dayjs(e.endTime);

  return {
    ...e,
    fromDate: from,
    toDate: to,
    startTime: from.hour(),
    endTime: to.hour(),
    startDate: from.toDate(),
    endDate: to.toDate(),
  };
}

export default function AvailabilityPage({
  LayoutComponent = AppLayoutDoctor,
  disableRemote = false,
  localEvents = [],
  localSlots = [],
  medecinIdOverride = null,
}) {
  const { t } = useTranslation('availability');

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rawSlots, setRawSlots] = useState([]);

  const availabilityPeriodsRef = useRef(null);
  const loadSlotsRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const scrollLockedRef = useRef(true);
  const rafRef = useRef(null);

  const doctorState = useSelector((state) => state.doctor || {});
  const medecinId = disableRemote
    ? null
    : medecinIdOverride || doctorState?.user?.id || doctorState?.user?.doctorId || null;

  const {
    slots,
    loadingSlots,
    loadingCreate,
    loadSlots,
    clearAllErrors,
    clearAllSuccess,
  } = useDisponibility({
    medecinId,
    date: selectedDate.toDate(),
  });

  const { events: reduxEvents } = useEvents({
    doctorIdOverride: medecinIdOverride,
    disableDoctorBootstrap: Boolean(medecinIdOverride),
  });

  const events = useMemo(() => {
    if (disableRemote) {
      return (localEvents || []).map((event) =>
        normalizeEvent({
          ...event,
          startTime: event.startTime || `${event.date}T${event.time || "09:00"}:00`,
          endTime: event.endTime || `${event.date}T${event.endTime || "10:00"}:00`,
        })
      );
    }
    return (reduxEvents || []).filter((e) => !!e?.startTime).map(normalizeEvent);
  }, [reduxEvents, disableRemote, localEvents]);

  useEffect(() => {
    if (!disableRemote) return;
    setRawSlots(localSlots || []);
  }, [disableRemote, localSlots]);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      const prev = history.scrollRestoration;
      history.scrollRestoration = 'manual';

      return () => {
        history.scrollRestoration = prev;
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const scrollGuard = () => {
      if (scrollLockedRef.current) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    };

    window.addEventListener('scroll', scrollGuard, { passive: true });

    const timer = setTimeout(() => {
      scrollLockedRef.current = false;
      window.removeEventListener('scroll', scrollGuard);
    }, SCROLL_LOCK_DURATION);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', scrollGuard);
    };
  }, []);

  const saveScroll = useCallback(() => {
    if (!scrollLockedRef.current) {
      scrollPositionRef.current = window.scrollY;
    }
  }, []);

  const restoreScroll = useCallback(() => {
    if (scrollLockedRef.current) return;

    const target = scrollPositionRef.current;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      window.scrollTo({ top: target, behavior: 'instant' });
    });
  }, []);

  useEffect(() => {
    if (disableRemote) return;
    if (!medecinId || !selectedDate) return;

    const key = `${medecinId}_${selectedDate.format('YYYY-MM-DD')}`;

    if (loadSlotsRef.current === key) return;

    loadSlotsRef.current = key;
    saveScroll();
    loadSlots(medecinId, selectedDate.toDate());
  }, [medecinId, selectedDate, loadSlots, saveScroll, disableRemote]);

  useEffect(() => {
    if (!loadingSlots) {
      restoreScroll();
    }
  }, [loadingSlots, restoreScroll]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      clearAllErrors();
      clearAllSuccess();
    };
  }, [clearAllErrors, clearAllSuccess]);

  const handleDateSelect = useCallback(
    (date) => {
      saveScroll();
      loadSlotsRef.current = null;
      setSelectedDate(dayjs.isDayjs(date) ? date : dayjs(date));
    },
    [saveScroll]
  );

  const handleOpenAddPeriod = useCallback(() => {
    availabilityPeriodsRef.current?.openAddPeriod?.();
  }, []);

  const slotDuration = slots?.[0]?.slotDuration ?? 30;

  return (
    <LayoutComponent>
      <div className="avail-page">
        <div className="avail-page__inner">
          <div className="avail-layout">
            {/* ── Colonne gauche : WeekView (ex-avail-main) ─────────── */}
            <div className="avail-sidebar">
              <WeekView
                medecinId={medecinId}
                events={events}
                rawSlots={rawSlots}
                slotDuration={slotDuration}
              />
            </div>

            {/* ── Colonne droite : CalendarView + Periods/Events (ex-sidebar) ─ */}
            <div className="avail-main">
              {!disableRemote && (loadingSlots || loadingCreate) && (
                <div className="avail-loading-row">
                  <span className="avail-loading-badge">
                    {t('page.loading')}
                  </span>
                </div>
              )}

              

              <div className="avail-views-stack">
                <CalendarView
                  medecinId={medecinId}
                  events={events}
                  slotDuration={slotDuration}
                  onDateSelect={handleDateSelect}
                  rawSlots={rawSlots}
                  onRequestAddPeriod={handleOpenAddPeriod}
                />

                <div className="avail-bottom-row">
                  {disableRemote ? (
                    <section className="avail-sidebar__periods-card" style={{ padding: 16 }}>
                      <div className="avail-sidebar__periods-title">{t('page.localPreviewTitle')}</div>
                      <p className="avail-sidebar__periods-description">
                        {t('page.localPreviewDescription')}
                      </p>
                    </section>
                  ) : (
                    <AvailabilityPeriodsList
                      ref={availabilityPeriodsRef}
                      medecinId={medecinId}
                      onSlotsLoaded={setRawSlots}
                    />
                  )}

                  <EventsList
                    events={events}
                    onDeleteEvent={() => {}}
                    selectedDate={selectedDate}
                    doctorIdOverride={medecinIdOverride}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutComponent>
  );
}